/**
 * "Generate tests" on the AI Service Testing tab (task 1185).
 *
 * The tab's ONE generate entry point, rendered by the Tests section in exactly
 * one place: the empty-state card while there are no tests (primary), the
 * section header once there are (secondary). The click IS the request (task
 * 1200): through the shared `useAssistantHandoff` hook it opens the Assistant
 * sidebar, sends `GENERATE_TESTS_PROMPT` as the User's own first turn of a NEW
 * thread, pulses the composer, and holds a sent state for about a second
 * (check icon, "Sent to Assistant", disabled), exactly as Suggest criteria
 * does. The Assistant's dispatcher routes the request to its test tier, which
 * saves the scenarios during the turn, so the Tests list refreshes with them.
 * Guidance ("add multilingual cases") is a follow-up in the same thread. There
 * is no second generator on the page.
 *
 * With no Assistant provider mounted (the sidebar flags are off) there is
 * nothing for the button to open, so it renders nothing rather than a control
 * that cannot act — the same rule `CreateInterfaceButton` follows.
 */
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useAssistantHandoff } from '@/hooks/useAssistantHandoff';

/**
 * The one prompt. Exported so the Assistant sidebar's test-turn card (task
 * 1188) can reference the same phrasing the button sends.
 */
export const GENERATE_TESTS_PROMPT = 'Generate test scenarios for this AI Service';

interface GenerateTestsButtonProps {
  /** `empty` = the empty-state card's primary button; `header` = the section header's secondary one. */
  placement: 'empty' | 'header';
}

const PLACEMENT_CLASS: Record<GenerateTestsButtonProps['placement'], string> = {
  empty:
    'mt-4 inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:cursor-default disabled:hover:bg-indigo-600',
  header:
    'inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-white dark:bg-gray-700 border border-indigo-300 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-gray-600 disabled:cursor-default disabled:hover:bg-white dark:disabled:hover:bg-gray-700',
};

export function GenerateTestsButton({ placement }: GenerateTestsButtonProps) {
  // Off, the click places the prompt in the composer unsent (task 1215).
  const autoSubmit = useFlag('innovate.fe.testing.assistant-handoff');
  const { assistant, sent, handoff } = useAssistantHandoff(GENERATE_TESTS_PROMPT, { autoSubmit });
  if (!assistant) return null;

  const iconClass = `${placement === 'empty' ? 'h-4 w-4 mr-2' : 'h-4 w-4 mr-1.5'} transition-opacity motion-reduce:transition-none`;

  return (
    <button
      type="button"
      data-testid={`tests-generate-assistant-${placement}`}
      data-sent={sent ? 'true' : undefined}
      onClick={handoff}
      disabled={sent}
      className={`${PLACEMENT_CLASS[placement]} transition-colors motion-reduce:transition-none`}
    >
      {sent ? (
        <CheckIcon data-testid={`tests-generate-assistant-${placement}-sent-icon`} className={iconClass} aria-hidden="true" />
      ) : (
        <SparklesIcon className={iconClass} aria-hidden="true" />
      )}
      {sent ? 'Sent to Assistant' : 'Generate scenarios'}
    </button>
  );
}

export default GenerateTestsButton;
